import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { styled, alpha, useTheme } from '@mui/material/styles';
import { Box, Link, Drawer, Typography, Avatar } from '@mui/material';

import useAccount from '../../../_mock/account';
import useResponsive from '../../../hooks/useResponsive';
import Logo from '../../../components/logo';
import Scrollbar from '../../../components/scrollbar';
import NavSection from '../../../components/nav-section';
import navConfig from './config';
import adminConfig from './adminConfig';

const NAV_WIDTH = 280;

// ----------------------------------------------------------------------

const StyledAccount = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2, 2.5),
  borderRadius: theme.shape.borderRadius * 1.5,
  backgroundColor:
    theme.palette.mode === 'light'
      ? alpha(theme.palette.common.black, 0.10)
      : alpha(theme.palette.common.white, 0.06),
}));

// ----------------------------------------------------------------------

Nav.propTypes = {
  openNav: PropTypes.bool,
  onCloseNav: PropTypes.func,
};

export default function Nav({ openNav, onCloseNav }) {
  const theme = useTheme();
  const { pathname } = useLocation();
  const account = useAccount();
  const isDesktop = useResponsive('up', 'lg');

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
  }, [pathname]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': {
          height: 1,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Logo */}
      <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
        <Logo />
      </Box>

      {/* Account */}
      <Box sx={{ mb: 1, mx: 1 }}>
        <Link underline="none">
          <StyledAccount>
            <Avatar src={account.photoURL} alt={account.displayName} />

            <Box sx={{ ml: 2 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: theme.palette.common.white,
                  fontSize: 16,
                  textTransform: 'capitalize',
                }}
              >
                {account.displayName}
              </Typography>
            </Box>
          </StyledAccount>
        </Link>
      </Box>

      {/* Nav Items */}
      {localStorage.getItem('role') === 'admin' ? (
        <NavSection data={adminConfig} />
      ) : (
        <NavSection data={navConfig} />
      )}

      <Box sx={{ flexGrow: 1 }} />

      {/* Help Card */}
      <Box
        sx={{
          px: 2,
          py: 2,
          mx: 2,
          borderRadius: 2,
          textAlign: 'center',
          backgroundColor:
            theme.palette.mode === 'light'
              ? alpha('#ffffff', 0.04)
              : alpha('#ffffff', 0.04),
          border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
        }}
      >
        <Typography
          variant="body1"
          sx={{ color: theme.palette.common.white, fontWeight: 700 }}
        >
          Need Help? Talk to Us
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: alpha(theme.palette.common.white, 0.85),
            lineHeight: 1.5,
            mt: 1,
          }}
        >
          +91 9024862242<br />
          +91 7568470833<br />
          support@watchnod.com<br />
          <a
            href="https://calendly.com/watchnod-support"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: theme.palette.common.white,
              textDecoration: 'underline',
            }}
          >
            Schedule a meet with Watchnod Support Team
          </a>
        </Typography>
      </Box>

      {/* Footer */}
      <Typography
        variant="caption"
        align="center"
        sx={{
          color: alpha(theme.palette.common.white, 0.7),
          m: 2,
          display: 'block',
        }}
      >
        © 2024 WatchNod as Tylect Technologies Pvt Ltd, all rights reserved.
      </Typography>
    </Scrollbar>
  );

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: NAV_WIDTH },
      }}
    >
      {isDesktop ? (
        <Drawer
          open
          variant="permanent"
          PaperProps={{
            sx: {
              width: NAV_WIDTH,
              bgcolor: theme.palette.mode === 'light' ? theme.palette.primary.main : theme.palette.background.default,
              borderRightStyle: 'dashed',
            },
          }}
        >
          {renderContent}
        </Drawer>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          ModalProps={{
            keepMounted: true,
          }}
          PaperProps={{
            sx: { width: NAV_WIDTH, bgcolor: theme.palette.mode === 'light' ? theme.palette.primary.main : theme.palette.background.default, },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}
